if (!customElements.get("fitment-form")) {
  class FitmentForm extends HTMLElement {
    constructor() {
      super();

      this.fitmentParam = "filter.p.m.custom.vehicle_fitment";
      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.collectionSelect = this.querySelector("select.dropdown-collection");
      this.collectionInput = this.querySelector("input.input-collection");
      this.selects = Array.from(this.querySelectorAll("select.dropdown-filter"));
      this.cardContainer = this.querySelector(".fitment-card-results");
      this.cardList = this.querySelector(".fitment-card-list");
      this.actionDock = this.querySelector(".fitment-action-dock");
      this.actionDockStatus = this.actionDock?.querySelector(".fitment-action-dock__status") || null;
      this.actionDockActions = this.actionDock?.querySelector(".fitment-action-dock__actions") || null;
      this.selectedFitment = null;
      this.pendingSelectionKey = "vehicle_fitment_pending_selection";
      this.activeFitmentActionLabel = this.translate("active_vehicle");
      this.viewKitsActionLabel = this.translate("view_kits");
      this.actionDockPrompt = this.translate("select_vehicle_to_continue");
      this.indexReady = false;
      this.lastPrefillPath = null;

      this.ensureCardContainer();
      this.ensureActionDock();
      this.setupEventListeners();
      this.setIndexLoading(true);
      this.whenFitmentIndexReady().then(() => {
        this.indexReady = true;
        this.setIndexLoading(false);
        this.populateCollectionFiltersWithEnhancement(true);
      });
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();

        const action = this.form.getAttribute("action");
        if (action) {
          window.location = action;
        }
      });

      this.collectionSelect?.addEventListener("change", () => {
        this.clearSelectedFitment();
        this.populateCollectionFiltersWithEnhancement(true);
      });

      this.selects.forEach((select) => {
        select.addEventListener("change", this.onFilterChangeWithEnhancement.bind(this));
      });

      window.addEventListener("vehicle-fitment-index:ready", () => {
        this.indexReady = true;
        this.setIndexLoading(false);
        this.populateCollectionFiltersWithEnhancement(true);
      });

      let currentUrl = window.location.href;
      const urlChangeHandler = () => {
        if (window.location.href === currentUrl) {
          return;
        }

        currentUrl = window.location.href;
        if (
          this.lastPrefillPath &&
          !new URL(window.location).searchParams.has(this.fitmentParam) &&
          this.prefillFromFitmentPath(this.lastPrefillPath)
        ) {
          return;
        }

        this.populateCollectionFiltersWithEnhancement(true);
      };

      window.addEventListener("popstate", urlChangeHandler);
      window.addEventListener("hashchange", urlChangeHandler);
      this.urlPoll = window.setInterval(urlChangeHandler, 1000);
    }

    ensureCardContainer() {
      if (!this.cardContainer) {
        this.cardContainer = document.createElement("div");
        this.cardContainer.className = "fitment-card-results";
        this.cardContainer.hidden = true;

        const heading = document.createElement("div");
        heading.className = "fitment-card-results-heading";
        heading.textContent = this.translate("select_vehicle_configuration");

        this.cardList = document.createElement("div");
        this.cardList.className = "fitment-card-list";

        this.cardContainer.append(heading, this.cardList);

        const buttonGroup = this.button?.closest(".form-group");
        if (buttonGroup) {
          buttonGroup.before(this.cardContainer);
        } else {
          this.form.append(this.cardContainer);
        }
      }

      if (!this.cardList) {
        this.cardList = this.cardContainer.querySelector(".fitment-card-list");
      }

      this.cardList?.setAttribute("role", "radiogroup");
      this.cardList?.setAttribute("aria-label", this.translate("vehicle_fitment_options"));
    }

    translate(key, replacements = {}) {
      return window.vehicleFitmentText?.(key, replacements) || key;
    }

    ensureActionDock() {
      if (!this.cardContainer) {
        return;
      }

      if (!this.actionDock) {
        this.actionDock = document.createElement("div");
        this.actionDock.className = "fitment-action-dock";
        this.actionDock.hidden = true;
        this.actionDock.setAttribute("aria-live", "polite");

        this.actionDockStatus = document.createElement("div");
        this.actionDockStatus.className = "fitment-action-dock__status";

        this.actionDockActions = document.createElement("div");
        this.actionDockActions.className = "fitment-action-dock__actions";
        this.actionDockActions.hidden = true;

        this.actionDock.append(this.actionDockStatus, this.actionDockActions);
        this.cardContainer.append(this.actionDock);
      }

      if (!this.actionDockStatus) {
        this.actionDockStatus = this.actionDock.querySelector(".fitment-action-dock__status");
      }

      if (!this.actionDockActions) {
        this.actionDockActions = this.actionDock.querySelector(".fitment-action-dock__actions");
      }
    }

    get fitments() {
      return Array.isArray(window.vehicleFitmentDiscoveryIndex)
        ? window.vehicleFitmentDiscoveryIndex
        : Array.isArray(window.vehicleFitmentIndex)
          ? window.vehicleFitmentIndex
          : [];
    }

    whenFitmentIndexReady() {
      if (window.vehicleFitmentIndexReady?.then) {
        return window.vehicleFitmentIndexReady;
      }

      if (Array.isArray(window.vehicleFitmentIndex)) {
        return Promise.resolve(window.vehicleFitmentIndex);
      }

      return new Promise((resolve) => {
        window.addEventListener("vehicle-fitment-index:ready", () => resolve(window.vehicleFitmentIndex || []), {
          once: true,
        });
      });
    }

    getSelect(label) {
      return this.selects.find((select) => select.dataset.label === label);
    }

    getSelectedOption(label) {
      const select = this.getSelect(label);
      return select?.selectedOptions?.[0] ?? null;
    }

    getSelectedValue(label) {
      return this.getSelect(label)?.value || "";
    }

    getSelectedLabel(label) {
      const option = this.getSelectedOption(label);
      return option && option.value ? option.textContent.trim() : "";
    }

    findFitmentById(fitmentId) {
      return this.fitments.find((fitment) => String(fitment.id) === String(fitmentId));
    }

    uniqueOptions(items, getOption, sortFn) {
      const byValue = new Map();

      items.forEach((item) => {
        const option = getOption(item);
        if (option?.value && !byValue.has(option.value)) {
          byValue.set(option.value, option);
        }
      });

      const options = Array.from(byValue.values());
      return sortFn ? options.sort(sortFn) : options.sort((a, b) => a.label.localeCompare(b.label));
    }

    optionElement(option) {
      const element = document.createElement("option");
      element.value = option.value;
      element.textContent = option.label;
      element.dataset.paramName = option.paramName;
      element.dataset.count = String(option.count || 1);
      if (option.id) {
        element.dataset.id = option.id;
      }
      return element;
    }

    replaceOptions(select, options, selectedValue = "") {
      this.selectRemoveOptions(select);
      select.append(...options.map((option) => this.optionElement(option)));
      const preserved = this.selectOptionValue(select, selectedValue);

      if (!preserved) {
        select.querySelector("option.default").selected = true;
      }

      return preserved;
    }

    enableSelect(select, enabled) {
      if (!select) {
        return;
      }

      select.disabled = !enabled;
      select.closest(".form-group")?.classList.toggle("disabled", !enabled);
    }

    populateCollectionFiltersWithEnhancement() {
      this.clearSelectedFitment();
      this.clearFitmentCards();

      if (!this.indexReady) {
        return;
      }

      const makeSelect = this.getSelect("Make");
      const modelSelect = this.getSelect("Model");
      const yearSelect = this.getSelect("Year");
      const engineSelect = this.getSelect("Engine");

      if (!this.fitments.length || !makeSelect || !modelSelect || !yearSelect) {
        return;
      }

      const makeOptions = this.uniqueOptions(
        this.fitments,
        (fitment) => ({
          id: fitment.make.id,
          value: fitment.make.gid,
          label: fitment.make.name,
          paramName: "filter.p.m.fitment.make",
        }),
      );

      this.replaceOptions(makeSelect, makeOptions);
      this.enableSelect(makeSelect, makeOptions.length > 0);

      [modelSelect, yearSelect, engineSelect].forEach((select) => {
        this.selectRemoveOptions(select);
        this.enableSelect(select, false);
      });
    }

    prefillFromFitmentId(fitmentId, path = {}) {
      const fitment = this.findFitmentById(fitmentId);
      if (!fitment) {
        return false;
      }

      return this.prefillFromFitmentPath({
        fitmentId: fitment.id,
        makeGid: fitment.make.gid,
        modelGid: fitment.model.gid,
        yearGid: fitment.year?.gid || "",
        engineValue: this.normalizedEngine(fitment).value,
        ...path,
      });
    }

    prefillFromFitmentPath(path = {}) {
      const fitment = this.findFitmentById(path.fitmentId);
      if (!fitment) {
        return false;
      }

      this.populateCollectionFiltersWithEnhancement();

      const makeValue = path.makeGid || fitment.make.gid;
      const modelValue = path.modelGid || fitment.model.gid;
      const yearValue = path.yearGid || fitment.year?.gid || "";
      const engineValue = path.engineValue || this.normalizedEngine(fitment).value;

      if (!this.selectOptionValue("Make", makeValue)) {
        return false;
      }

      this.populateModels();
      if (!this.selectOptionValue("Model", modelValue)) {
        return false;
      }

      this.populateYears();
      if (!this.selectOptionValue("Year", yearValue)) {
        return false;
      }

      this.populateEngines();
      if (this.getSelect("Engine") && !this.selectOptionValue("Engine", engineValue)) {
        return false;
      }

      this.populateFitmentCards();
      this.lastPrefillPath = {
        fitmentId: fitment.id,
        makeGid: makeValue,
        modelGid: modelValue,
        yearGid: yearValue,
        engineValue,
      };
      return true;
    }

    onFilterChangeWithEnhancement(event) {
      const label = event.target.dataset.label;
      this.lastPrefillPath = null;
      this.clearSelectedFitment();

      if (label === "Make") {
        this.populateModels();
      }

      if (label === "Model") {
        this.populateYears();
      }

      if (label === "Year") {
        if (this.getSelect("Engine")) {
          this.populateEngines();
        } else {
          this.populateFitmentCards();
        }
      }

      if (label === "Engine") {
        this.populateFitmentCards();
      }
    }

    populateModels() {
      const modelSelect = this.getSelect("Model");
      const yearSelect = this.getSelect("Year");
      const engineSelect = this.getSelect("Engine");
      const makeGid = this.getSelectedValue("Make");
      const previousModel = this.getSelectedValue("Model");
      const previousYear = this.getSelectedValue("Year");
      const previousEngine = this.getSelectedValue("Engine");

      this.clearFitmentCards();
      this.selectRemoveOptions(yearSelect);
      this.selectRemoveOptions(engineSelect);
      this.enableSelect(modelSelect, false);
      this.enableSelect(yearSelect, false);
      this.enableSelect(engineSelect, false);

      const matchingFitments = this.fitments.filter((fitment) => fitment.make.gid === makeGid);
      const modelOptions = this.uniqueOptions(
        matchingFitments,
        (fitment) => ({
          id: fitment.model.id,
          value: fitment.model.gid,
          label: fitment.model.name,
          paramName: "filter.p.m.fitment.model",
        }),
      );

      const modelPreserved = this.replaceOptions(modelSelect, modelOptions, previousModel);
      this.enableSelect(modelSelect, modelOptions.length > 0);

      if (modelPreserved) {
        this.populateYears({ preferredYear: previousYear, preferredEngine: previousEngine });
      }
    }

    populateYears({ preferredYear = this.getSelectedValue("Year"), preferredEngine = this.getSelectedValue("Engine") } = {}) {
      const yearSelect = this.getSelect("Year");
      const engineSelect = this.getSelect("Engine");
      const makeGid = this.getSelectedValue("Make");
      const modelGid = this.getSelectedValue("Model");

      this.clearFitmentCards();
      this.selectRemoveOptions(engineSelect);
      this.enableSelect(yearSelect, false);
      this.enableSelect(engineSelect, false);

      const matchingFitments = this.fitments.filter(
        (fitment) => fitment.make.gid === makeGid && fitment.model.gid === modelGid,
      );
      const yearOptions = [];

      matchingFitments.forEach((fitment) => {
        const year = fitment.year;
        yearOptions.push({
          id: year.id,
          value: year.gid,
          label: year.name,
          number: Number(year.number || year.name),
          paramName: "filter.p.m.fitment.year",
        });
      });

      const uniqueYears = this.uniqueOptions(
        yearOptions,
        (year) => year,
        (a, b) => a.number - b.number,
      );

      const yearPreserved = this.replaceOptions(yearSelect, uniqueYears, preferredYear);
      this.enableSelect(yearSelect, uniqueYears.length > 0);

      if (yearPreserved) {
        this.populateEngines({ preferredEngine });
      }
    }

    populateEngines({ preferredEngine = this.getSelectedValue("Engine") } = {}) {
      const engineSelect = this.getSelect("Engine");

      if (!engineSelect) {
        this.populateFitmentCards();
        return;
      }

      const makeGid = this.getSelectedValue("Make");
      const modelGid = this.getSelectedValue("Model");
      const yearGid = this.getSelectedValue("Year");

      this.clearFitmentCards();
      this.enableSelect(engineSelect, false);

      const matchingFitments = this.fitments.filter((fitment) => {
        return (
          fitment.make.gid === makeGid &&
          fitment.model.gid === modelGid &&
          fitment.year?.gid === yearGid
        );
      });

      const engineOptions = this.uniqueOptions(
        matchingFitments,
        (fitment) => {
          const engine = this.normalizedEngine(fitment);
          return {
            id: engine.id,
            value: engine.value,
            label: engine.name,
            paramName: "filter.p.m.fitment.engine",
          };
        },
      );

      const enginePreserved = this.replaceOptions(engineSelect, engineOptions, preferredEngine);
      this.enableSelect(engineSelect, engineOptions.length > 0);

      if (enginePreserved) {
        this.populateFitmentCards();
      } else if (engineOptions.length === 1) {
        engineSelect.value = engineOptions[0].value;
        this.populateFitmentCards();
      }
    }

    populateFitmentCards() {
      const makeGid = this.getSelectedValue("Make");
      const modelGid = this.getSelectedValue("Model");
      const yearGid = this.getSelectedValue("Year");
      const engineSelect = this.getSelect("Engine");
      const engineValue = this.getSelectedValue("Engine");

      const matchingFitments = this.fitments.filter((fitment) => {
        return (
          fitment.make.gid === makeGid &&
          fitment.model.gid === modelGid &&
          fitment.year?.gid === yearGid &&
          (!engineSelect || this.normalizedEngine(fitment).value === engineValue)
        );
      });

      this.renderFitmentCards(matchingFitments);
    }

    normalizedEngine(fitment) {
      const sourceName = fitment.engine?.name || "Any";
      const name = sourceName === "Any" ? this.translate("any") : sourceName;

      return {
        id: fitment.engine?.id || "",
        gid: fitment.engine?.gid || "",
        name,
        value: String(fitment.engine?.value || sourceName).trim().toLowerCase(),
      };
    }

    renderFitmentCards(fitments) {
      if (!this.cardContainer || !this.cardList) {
        return;
      }

      this.clearSelectedFitment(false);
      this.cardList.innerHTML = "";
      this.cardContainer.hidden = fitments.length === 0 && !this.hasCompleteVehiclePath();

      if (!this.hasCompleteVehiclePath()) {
        this.updateSelectedVehicleSummary();
        this.renderActionDock(false);
        return;
      }

      this.updateSelectedVehicleSummary();

      fitments
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((fitment) => {
          this.cardList.append(this.createFitmentCard(fitment));
        });

      this.cardList.append(this.createRequestCard());
      this.syncFitmentCardSelection();
      this.renderActionDock(fitments.length > 0);
    }

    updateSelectedVehicleSummary() {
      const heading = this.cardContainer?.querySelector(".fitment-card-results-heading");
      if (!heading) {
        return;
      }

      const selection = ["Year", "Make", "Model", "Engine"]
        .map((label) => this.getSelectedLabel(label))
        .filter((label) => label && label !== this.translate("any"))
        .join(" ");

      if (!selection) {
        heading.textContent = this.translate("select_vehicle_configuration");
        return;
      }

      const summary = document.createElement("strong");
      summary.className = "fitment-card-results-summary";
      summary.textContent = selection;
      heading.replaceChildren(this.translate("select_current_prefix"), summary, this.translate("configuration_below"));
    }

    clearFitmentCards() {
      if (!this.cardContainer || !this.cardList) {
        return;
      }

      this.clearSelectedFitment(false);
      this.cardList.innerHTML = "";
      this.cardContainer.hidden = true;
      this.renderActionDock(false);
    }

    createFitmentCard(fitment) {
      const card = document.createElement("div");
      card.className = "fitment-card js-fitment-card";
      card.dataset.fitmentId = fitment.id;
      card.dataset.fitmentGid = fitment.gid;
      card.setAttribute("role", "radio");
      card.setAttribute("aria-checked", "false");
      card.tabIndex = -1;
      card.addEventListener("click", () => this.selectFitmentCard(card, fitment));
      card.addEventListener("keydown", (event) => this.onFitmentCardKeydown(event));

      const image = fitment.imageUrl
        ? `<img src="${this.escapeAttribute(fitment.imageUrl)}" alt="" loading="lazy">`
        : '<span class="fitment-card-placeholder" aria-hidden="true"></span>';
      const headline = String(fitment.headline || fitment.name || "").trim();
      const notesText = String(fitment.notes || "").trim();
      const helperText = this.extractHelperText(fitment.helperText);
      const details = [
        [this.translate("trim"), fitment.trim, "fitment-card-detail--primary"],
        [this.translate("existing_lift"), fitment.existingLift],
        [this.translate("generation"), fitment.generation],
        [this.translate("body"), fitment.body],
      ].map(([label, value, modifier]) => {
        const text = String(value || "").trim();
        return text
          ? `<span class="fitment-card-detail${modifier ? ` ${modifier}` : ""}"><span class="fitment-card-detail-label">${this.escapeHtml(label)}</span><span class="fitment-card-detail-value">${this.escapeHtml(text)}</span></span>`
          : "";
      }).join("");
      const hasStructuredGuidance = Boolean(fitment.headline || details);
      const isDefaultNote = notesText === "Shop compatible kits for this vehicle." || notesText === this.translate("fallback_note");
      const notes = notesText && !isDefaultNote
        ? `<span class="fitment-card-caption">${this.escapeHtml(notesText)}</span>`
        : !hasStructuredGuidance
          ? `<span class="fitment-card-caption">${this.escapeHtml(this.translate("fallback_note"))}</span>`
          : "";

      card.innerHTML = `
        <span class="fitment-card-media">${image}</span>
        <span class="fitment-card-body">
          <strong class="fitment-card-headline">${this.escapeHtml(headline)}</strong>
          ${details}
          ${notes}
        </span>
      `;

      const option = document.createElement("div");
      option.className = "fitment-card-option";
      option.append(card);

      if (!helperText) {
        return option;
      }

      const helperTarget = headline || this.translate("this_option");
      const moreInformationLabel = this.translate("more_information", { option: helperTarget });
      const hideInformationLabel = this.translate("hide_information", { option: helperTarget });
      const frame = document.createElement("div");
      frame.className = "fitment-card-frame";
      card.classList.add("fitment-card-choice");

      const helperToggle = document.createElement("button");
      helperToggle.type = "button";
      helperToggle.className = "fitment-card-helper-toggle";
      helperToggle.setAttribute("aria-expanded", "false");
      helperToggle.setAttribute("aria-label", moreInformationLabel);
      helperToggle.textContent = "?";

      const helper = document.createElement("div");
      helper.className = "fitment-card-helper";
      helper.hidden = true;
      helper.setAttribute("role", "region");
      helper.setAttribute("aria-label", this.translate("information_about", { option: helperTarget }));
      helper.textContent = helperText;

      helperToggle.addEventListener("click", () => {
        this.selectFitmentCard(card, fitment);
        const isExpanded = helperToggle.getAttribute("aria-expanded") === "true";
        helperToggle.setAttribute("aria-expanded", String(!isExpanded));
        helperToggle.setAttribute("aria-label", isExpanded ? moreInformationLabel : hideInformationLabel);
        helper.hidden = isExpanded;
      });

      frame.append(card, helperToggle, helper);
      option.replaceChildren(frame);
      return option;
    }

    renderActionDock(show) {
      this.ensureActionDock();

      if (!this.actionDock || !this.actionDockStatus || !this.actionDockActions) {
        return;
      }

      this.actionDock.hidden = !show;
      this.actionDockActions.innerHTML = "";

      if (!show) {
        this.actionDockStatus.textContent = "";
        this.actionDockActions.hidden = true;
        return;
      }

      if (!this.selectedFitment) {
        this.actionDockStatus.textContent = this.actionDockPrompt;
        this.actionDockActions.hidden = true;
        return;
      }

      this.actionDockStatus.textContent = this.translate("selected_vehicle", { vehicle: this.selectedFitment.name });
      this.actionDockActions.hidden = false;
      const context = this.fitmentActionContext();

      if (context === "collection") {
        this.actionDockActions.append(this.createViewKitsAction(this.selectedFitment, true));
        return;
      }

      if (context === "product") {
        this.actionDockActions.append(
          this.createActiveFitmentAction(this.selectedFitment, true),
          this.createViewKitsAction(this.selectedFitment, false),
        );
        return;
      }

      this.actionDockActions.append(
        this.createActiveFitmentAction(this.selectedFitment, false),
        this.createViewKitsAction(this.selectedFitment, true),
      );
    }

    createViewKitsAction(fitment, isPrimary) {
      const link = document.createElement("a");
      link.className = this.actionClassName(isPrimary);
      link.href = this.buildCollectionUrl(fitment.gid);
      link.textContent = this.viewKitsActionLabel;
      link.addEventListener("click", () => this.storePendingSelection(fitment));
      return link;
    }

    createActiveFitmentAction(fitment, isPrimary) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = this.actionClassName(isPrimary);
      button.textContent = this.activeFitmentActionLabel;
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.setActiveFitment(fitment, button);
      });
      return button;
    }

    actionClassName(isPrimary) {
      return `fitment-dock-action ${isPrimary ? "fitment-dock-action--primary" : "fitment-dock-action--secondary"}`;
    }

    fitmentActionContext() {
      const pathname = window.location.pathname || "";

      if (
        window.VehicleFitmentUrls?.isCollectionPath?.(pathname) ||
        /^\/collections\/[^/?#]+\/?$/.test(pathname)
      ) {
        return "collection";
      }

      if (this.closest(".product-info-sidebar")) {
        return "product";
      }

      return "discovery";
    }

    async setActiveFitment(fitment, button) {
      button.disabled = true;

      try {
        const metadata = {
          displayName: fitment.name,
          imageUrl: fitment.imageUrl || fitment.image || "",
          selectionPath: this.currentSelectionPath(fitment),
        };

        if (window.vehicleFitmentController?.setCurrentVehicle) {
          await window.vehicleFitmentController.setCurrentVehicle(fitment.id, metadata);
        } else {
          const timestamp = new Date().toISOString();
          window.ThemeStorage?.setJson?.("vehicle_fitment_history", {
            current: {
              id: String(fitment.id),
              firstSeen: timestamp,
              lastSeen: timestamp,
              ...metadata,
            },
            history: [],
          });
          window.refreshFitmentFormWraps?.();
        }
      } finally {
        button.disabled = false;
      }
    }

    createRequestCard() {
      const link = document.createElement("a");
      link.className = "fitment-card fitment-card-request js-fitment-request-card";
      link.href = this.buildVehicleRequestUrl();
      link.innerHTML = `
        <span class="fitment-card-body">
          <strong>${this.escapeHtml(this.translate("request_heading"))}</strong>
          <span class="fitment-card-caption">${this.escapeHtml(this.translate("request_body"))}</span>
        </span>
      `;
      return link;
    }

    selectFitmentCard(card, fitment) {
      this.selectedFitment = fitment;
      this.syncFitmentCardSelection(card);
      this.setFormUrl(this.buildCollectionUrl(fitment.gid));
      this.renderActionDock(true);
    }

    onFitmentCardKeydown(event) {
      const navigationKeys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"];
      if (!navigationKeys.includes(event.key)) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.currentTarget.click();
        }
        return;
      }

      const cards = Array.from(this.cardList?.querySelectorAll(".js-fitment-card[data-fitment-id]") || []);
      if (!cards.length) {
        return;
      }

      const currentIndex = cards.indexOf(event.currentTarget);
      if (currentIndex < 0) {
        return;
      }

      event.preventDefault();
      let nextIndex = currentIndex;

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % cards.length;
      } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + cards.length) % cards.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = cards.length - 1;
      }

      cards[nextIndex].focus();
      cards[nextIndex].click();
    }

    clearSelectedFitment(renderDock = true) {
      this.selectedFitment = null;
      this.form.setAttribute("action", "");
      if (this.button) {
        this.button.disabled = true;
      }
      this.syncFitmentCardSelection();

      if (renderDock) {
        this.renderActionDock(this.hasSupportedFitmentCards());
      }
    }

    syncFitmentCardSelection(selectedCard = null) {
      const cards = Array.from(this.cardList?.querySelectorAll(".js-fitment-card[data-fitment-id]") || []);
      const selectedId = this.selectedFitment ? String(this.selectedFitment.id) : "";

      cards.forEach((card, index) => {
        const isSelected = selectedCard ? card === selectedCard : Boolean(selectedId && card.dataset.fitmentId === selectedId);
        card.classList.toggle("is-selected", isSelected);
        card.closest(".fitment-card-frame")?.classList.toggle("is-selected", isSelected);
        card.setAttribute("aria-checked", isSelected ? "true" : "false");
        card.tabIndex = isSelected || (!selectedId && index === 0) ? 0 : -1;
      });
    }

    hasSupportedFitmentCards() {
      return Boolean(this.cardList?.querySelector(".js-fitment-card[data-fitment-id]"));
    }

    hasCompleteVehiclePath() {
      return Boolean(
        this.getSelectedValue("Make") &&
          this.getSelectedValue("Model") &&
          this.getSelectedValue("Year") &&
          (!this.getSelect("Engine") || this.getSelectedValue("Engine")),
      );
    }

    buildCollectionUrl(fitmentGid) {
      const collectionUrl =
        this.collectionSelect?.value ||
        this.collectionInput?.value ||
        "";

      if (window.VehicleFitmentUrls?.buildFitmentCollectionUrl) {
        return window.VehicleFitmentUrls.buildFitmentCollectionUrl(fitmentGid, {
          collectionUrl,
        });
      }

      const currentUrl = new URL(window.location);
      const url = new URL(collectionUrl || window.routes.all_products_url, window.requestShopUrl);
      url.search = currentUrl.search;
      url.searchParams.set(this.fitmentParam, fitmentGid);
      url.searchParams.delete("page");
      return url.toString();
    }

    buildVehicleRequestUrl() {
      const url = new URL("/pages/vehicle-request", window.requestShopUrl);

      [
        ["make", this.getSelectedLabel("Make")],
        ["model", this.getSelectedLabel("Model")],
        ["year", this.getSelectedLabel("Year")],
        ["engine", this.getSelectedLabel("Engine")],
      ].forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });

      url.searchParams.set("source", "fitment_selector");
      return url.toString();
    }

    currentSelectionPath(fitment) {
      return {
        fitmentId: fitment.id,
        makeGid: this.getSelectedValue("Make") || fitment.make.gid,
        modelGid: this.getSelectedValue("Model") || fitment.model.gid,
        yearGid: this.getSelectedValue("Year") || fitment.year?.gid || "",
        engineValue: this.getSelectedValue("Engine") || this.normalizedEngine(fitment).value,
      };
    }

    storePendingSelection(fitment) {
      window.ThemeStorage?.setJson?.(
        this.pendingSelectionKey,
        this.currentSelectionPath(fitment),
        "sessionStorage",
      );
    }

    setFormUrl(url) {
      this.form.setAttribute("action", url);

      if (this.button && this.selectedFitment) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      if (!select) {
        return;
      }

      select.querySelectorAll("option:not(.default)").forEach((option) => option.remove());
      select.querySelector("option.default").selected = true;
    }

    selectOptionValue(selectOrLabel, value) {
      const select = typeof selectOrLabel === "string" ? this.getSelect(selectOrLabel) : selectOrLabel;
      if (!select || !value) {
        return false;
      }

      const option = Array.from(select.options).find((item) => item.value === value);
      if (!option || option.disabled) {
        return false;
      }

      select.value = value;
      return true;
    }

    toggleLoading() {
      this.classList.toggle("is-loading");
      this.button?.classList.toggle("loading");
    }

    setIndexLoading(loading) {
      this.classList.toggle("is-loading", loading);
      this.selects.forEach((select) => this.enableSelect(select, !loading && !select.closest(".form-group")?.classList.contains("disabled")));
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (char) => {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;",
        }[char];
      });
    }

    extractHelperText(value) {
      try {
        const collect = (node) => {
          if (!node || typeof node !== "object") return [];
          if (node.type === "text" && typeof node.value === "string") return [node.value];
          return Array.isArray(node.children) ? node.children.flatMap(collect) : [];
        };
        return collect(JSON.parse(String(value || ""))).join(" ").replace(/\s+/g, " ").trim();
      } catch {
        return "";
      }
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  customElements.define("fitment-form", FitmentForm);
}
